// src/components/workspace/costing/LaborCategoriesPage.tsx
// Dedicated page for managing company labor categories with SCA rate lookups

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, ArrowLeft, Plus, Trash2, Save, Loader2, Search,
  CheckCircle, AlertCircle, DollarSign, Sparkles, Download,
  Upload, Info, GraduationCap, Briefcase, Shield, Edit2, X
} from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import { formatCurrency } from '../../../lib/pricing/utils';
import toast from 'react-hot-toast';

interface LaborCategory {
  id: string;
  title: string;
  description: string;
  base_hourly_rate: number;
  gsa_rate: number | null;
  sca_occupation_code: string | null;
  sca_occupation_title: string | null;
  years_experience_min: number;
  years_experience_max: number | null;
  education_requirement: string | null;
  clearance_level: string | null;
  functional_area: string | null;
  is_key_personnel: boolean;
  sort_order: number;
  is_active: boolean;
  isNew?: boolean;
  isEditing?: boolean;
}

interface SCARate {
  occupation_code: string;
  occupation_title: string;
  hourly_rate: number;
  health_welfare_rate: number;
  fringe_rate: number;
  area_description: string;
}

interface IndirectRates {
  fringe_rate: number;
  overhead_rate: number;
  ga_rate: number;
  profit_rate: number;
}

interface ContractTypeTemplate {
  contract_type: string;
  display_name: string;
  typical_labor_categories: any[];
}

// Default indirect rates if none configured
const DEFAULT_INDIRECT_RATES: IndirectRates = {
  fringe_rate: 32,
  overhead_rate: 45,
  ga_rate: 12,
  profit_rate: 10
};

// Common labor categories suggestions by industry
const SUGGESTED_CATEGORIES = {
  'it_services': [
    { title: 'Program Manager', baseRate: 95, gsaRate: 150, scaCode: '01020', experience: '10+', education: "Master's Degree" },
    { title: 'Project Manager', baseRate: 85, gsaRate: 125, scaCode: '01021', experience: '7+', education: "Bachelor's Degree" },
    { title: 'Senior Software Engineer', baseRate: 85, gsaRate: 145, scaCode: '14074', experience: '8+', education: "Bachelor's Degree" },
    { title: 'Software Engineer', baseRate: 65, gsaRate: 110, scaCode: '14073', experience: '4+', education: "Bachelor's Degree" },
    { title: 'Systems Administrator', baseRate: 55, gsaRate: 95, scaCode: '14041', experience: '5+', education: "Bachelor's Degree" },
    { title: 'Help Desk Technician', baseRate: 35, gsaRate: 55, scaCode: '14043', experience: '2+', education: 'Associate Degree' },
    { title: 'Cybersecurity Analyst', baseRate: 75, gsaRate: 125, scaCode: '14071', experience: '5+', education: "Bachelor's Degree" },
    { title: 'Data Analyst', baseRate: 55, gsaRate: 90, scaCode: '14042', experience: '3+', education: "Bachelor's Degree" },
    { title: 'Technical Writer', baseRate: 45, gsaRate: 75, scaCode: '01313', experience: '3+', education: "Bachelor's Degree" },
    { title: 'Quality Assurance Specialist', baseRate: 50, gsaRate: 85, scaCode: '13070', experience: '4+', education: "Bachelor's Degree" }
  ],
  'professional_services': [
    { title: 'Senior Consultant', baseRate: 75, gsaRate: 115, scaCode: '01113', experience: '8+', education: "Master's Degree" },
    { title: 'Consultant', baseRate: 55, gsaRate: 85, scaCode: '01112', experience: '4+', education: "Bachelor's Degree" },
    { title: 'Business Analyst', baseRate: 52, gsaRate: 80, scaCode: '01111', experience: '3+', education: "Bachelor's Degree" },
    { title: 'Administrative Assistant', baseRate: 25, gsaRate: 45, scaCode: '01011', experience: '2+', education: 'High School' }
  ],
  'maintenance': [
    { title: 'Maintenance Supervisor', baseRate: 45, gsaRate: 75, scaCode: '23370', experience: '5+', education: 'Trade Certificate' },
    { title: 'HVAC Technician', baseRate: 38, gsaRate: 65, scaCode: '23130', experience: '3+', education: 'Trade Certificate' },
    { title: 'Electrician', baseRate: 40, gsaRate: 68, scaCode: '23100', experience: '4+', education: 'Trade Certificate' },
    { title: 'Plumber', baseRate: 38, gsaRate: 65, scaCode: '23430', experience: '3+', education: 'Trade Certificate' },
    { title: 'General Maintenance Worker', baseRate: 28, gsaRate: 45, scaCode: '23370', experience: '1+', education: 'High School' },
    { title: 'Custodian/Janitor', baseRate: 18, gsaRate: 30, scaCode: '11150', experience: '0+', education: 'None Required' }
  ],
  'transportation': [
    { title: 'Transportation Manager', baseRate: 42, gsaRate: 70, scaCode: '31361', experience: '5+', education: "Bachelor's Degree" },
    { title: 'Dispatcher', baseRate: 28, gsaRate: 45, scaCode: '31090', experience: '2+', education: 'High School' },
    { title: 'CDL Driver Class A', baseRate: 32, gsaRate: 52, scaCode: '31362', experience: '2+', education: 'CDL License' },
    { title: 'CDL Driver Class B', baseRate: 28, gsaRate: 45, scaCode: '31363', experience: '1+', education: 'CDL License' },
    { title: 'Vehicle Mechanic', baseRate: 35, gsaRate: 58, scaCode: '23180', experience: '3+', education: 'Trade Certificate' }
  ],
  'food_services': [
    { title: 'Food Service Manager', baseRate: 38, gsaRate: 62, scaCode: '07041', experience: '5+', education: "Associate's Degree" },
    { title: 'Executive Chef', baseRate: 35, gsaRate: 58, scaCode: '07042', experience: '5+', education: 'Culinary Degree' },
    { title: 'Cook', baseRate: 22, gsaRate: 35, scaCode: '07041', experience: '2+', education: 'Food Handler Cert' },
    { title: 'Food Service Worker', baseRate: 16, gsaRate: 26, scaCode: '07010', experience: '0+', education: 'Food Handler Cert' }
  ],
  'security': [
    { title: 'Security Manager', baseRate: 42, gsaRate: 70, scaCode: '27101', experience: '7+', education: "Bachelor's Degree" },
    { title: 'Security Supervisor', baseRate: 32, gsaRate: 52, scaCode: '27102', experience: '3+', education: 'High School' },
    { title: 'Armed Security Officer', baseRate: 28, gsaRate: 45, scaCode: '27102', experience: '2+', education: 'Guard License' },
    { title: 'Unarmed Security Officer', baseRate: 22, gsaRate: 35, scaCode: '27101', experience: '1+', education: 'Guard License' }
  ]
};

const CLEARANCE_LEVELS = [
  { value: 'none', label: 'None Required' },
  { value: 'public_trust', label: 'Public Trust' },
  { value: 'secret', label: 'Secret' },
  { value: 'top_secret', label: 'Top Secret' },
  { value: 'ts_sci', label: 'TS/SCI' }
];

export function LaborCategoriesPage({ embedded = false }: { embedded?: boolean } = {}) {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState<LaborCategory[]>([]);
  const [indirectRates, setIndirectRates] = useState<IndirectRates>(DEFAULT_INDIRECT_RATES);
  const [scaRates, setScaRates] = useState<SCARate[]>([]);
  const [contractTemplates, setContractTemplates] = useState<ContractTypeTemplate[]>([]);
  const [selectedIndustry, setSelectedIndustry] = useState<string>('it_services');
  const [searchTerm, setSearchTerm] = useState('');
  const [showSCALookup, setShowSCALookup] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  useEffect(() => {
    if (profile) {
      loadData();
    }
  }, [profile]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load labor categories
      const { data: laborData, error: laborError } = await supabase
        .from('company_labor_categories')
        .select('*')
        .eq('profile_id', profile?.id)
        .order('sort_order');

      if (laborError && laborError.code !== 'PGRST116') {
        console.error('Error loading labor categories:', laborError);
      }

      // Load indirect rates
      const { data: ratesData, error: ratesError } = await supabase
        .from('company_indirect_rates')
        .select('*')
        .eq('profile_id', profile?.id)
        .single();

      if (ratesError && ratesError.code !== 'PGRST116') {
        console.error('Error loading indirect rates:', ratesError);
      }

      // Load SCA rates for lookups
      const { data: scaData, error: scaError } = await supabase
        .from('sca_wage_rates')
        .select('occupation_code, occupation_title, hourly_rate, health_welfare_rate, fringe_rate, area_description')
        .order('occupation_title');

      if (scaError && scaError.code !== 'PGRST116') {
        console.error('Error loading SCA rates:', scaError);
      }

      // Load contract type templates
      const { data: templateData, error: templateError } = await supabase
        .from('contract_type_templates')
        .select('contract_type, display_name, typical_labor_categories');

      if (templateError && templateError.code !== 'PGRST116') {
        console.error('Error loading templates:', templateError);
      }

      setCategories(laborData || []);
      if (ratesData) {
        setIndirectRates(ratesData);
      }
      setScaRates(scaData || []);
      setContractTemplates(templateData || []);

    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load labor categories');
    } finally {
      setLoading(false);
    }
  };

  const calculateWrapRate = (): number => {
    return (1 + indirectRates.fringe_rate / 100) *
           (1 + indirectRates.overhead_rate / 100) *
           (1 + indirectRates.ga_rate / 100) *
           (1 + indirectRates.profit_rate / 100);
  };

  const calculateLoadedRate = (baseRate: number): number => {
    return baseRate * calculateWrapRate();
  };

  const addCategory = () => {
    const newCategory: LaborCategory = {
      id: `new-${Date.now()}`,
      title: '',
      description: '',
      base_hourly_rate: 50,
      gsa_rate: null,
      sca_occupation_code: null,
      sca_occupation_title: null,
      years_experience_min: 0,
      years_experience_max: null,
      education_requirement: null,
      clearance_level: 'none',
      functional_area: null,
      is_key_personnel: false,
      sort_order: categories.length,
      is_active: true,
      isNew: true,
      isEditing: true
    };
    setCategories([...categories, newCategory]);
    setEditingId(newCategory.id);
  };

  const addFromSuggestion = (suggestion: any) => {
    const exists = categories.some(
      cat => cat.title.toLowerCase() === suggestion.title.toLowerCase()
    );
    if (exists) {
      toast.error('This labor category already exists');
      return;
    }

    const newCategory: LaborCategory = {
      id: `new-${Date.now()}`,
      title: suggestion.title,
      description: '',
      base_hourly_rate: suggestion.baseRate,
      gsa_rate: suggestion.gsaRate,
      sca_occupation_code: suggestion.scaCode,
      sca_occupation_title: null,
      years_experience_min: parseInt(suggestion.experience) || 0,
      years_experience_max: null,
      education_requirement: suggestion.education,
      clearance_level: 'none',
      functional_area: selectedIndustry,
      is_key_personnel: false,
      sort_order: categories.length,
      is_active: true,
      isNew: true,
      isEditing: false
    };
    setCategories([...categories, newCategory]);
    toast.success(`Added ${suggestion.title}`);
  };

  const updateCategory = (id: string, updates: Partial<LaborCategory>) => {
    setCategories(categories.map(cat =>
      cat.id === id ? { ...cat, ...updates } : cat
    ));
  };

  const deleteCategory = (id: string) => {
    setCategories(categories.filter(cat => cat.id !== id));
  };

  const applySCARate = (categoryId: string, scaRate: SCARate) => {
    updateCategory(categoryId, {
      sca_occupation_code: scaRate.occupation_code,
      sca_occupation_title: scaRate.occupation_title,
      base_hourly_rate: scaRate.hourly_rate + scaRate.fringe_rate
    });
    setShowSCALookup(false);
    toast.success(`Applied SCA rate: $${scaRate.hourly_rate}/hr + $${scaRate.fringe_rate} fringe`);
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      // Validate categories
      const invalidCategories = categories.filter(cat => !cat.title.trim());
      if (invalidCategories.length > 0) {
        toast.error('Please provide a title for all labor categories');
        return;
      }

      // Separate new and existing categories
      const newCategories = categories.filter(cat => cat.isNew);
      const existingCategories = categories.filter(cat => !cat.isNew);

      // Delete removed categories
      const { data: currentData } = await supabase
        .from('company_labor_categories')
        .select('id')
        .eq('profile_id', profile?.id);

      const currentIds = currentData?.map(c => c.id) || [];
      const keepIds = existingCategories.map(c => c.id);
      const deleteIds = currentIds.filter(id => !keepIds.includes(id));

      if (deleteIds.length > 0) {
        await supabase
          .from('company_labor_categories')
          .delete()
          .in('id', deleteIds);
      }

      // Insert new categories
      if (newCategories.length > 0) {
        const toInsert = newCategories.map(({ id, isNew, isEditing, ...cat }) => ({
          ...cat,
          profile_id: profile?.id
        }));

        const { error: insertError } = await supabase
          .from('company_labor_categories')
          .insert(toInsert);

        if (insertError) throw insertError;
      }

      // Update existing categories
      for (const cat of existingCategories) {
        const { id, isNew, isEditing, ...updates } = cat;
        const { error: updateError } = await supabase
          .from('company_labor_categories')
          .update(updates)
          .eq('id', id);

        if (updateError) throw updateError;
      }

      toast.success('Labor categories saved successfully!');
      await loadData(); // Reload to get fresh data with proper IDs

    } catch (error) {
      console.error('Error saving labor categories:', error);
      toast.error('Failed to save labor categories');
    } finally {
      setSaving(false);
    }
  };

  const filteredSuggestions = SUGGESTED_CATEGORIES[selectedIndustry as keyof typeof SUGGESTED_CATEGORIES] || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-bw-primary" />
      </div>
    );
  }

  return (
    <div className={embedded ? '' : 'max-w-6xl mx-auto p-6'}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          {!embedded && (
          <button
            onClick={() => navigate('/workspace/costing')}
            className="flex items-center text-sm text-bw-muted hover:text-bw-ink mb-2"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Costing Studio
          </button>
          )}
          <h1 className="text-2xl font-bold text-bw-ink flex items-center">
            <Users className="h-6 w-6 mr-2 text-bw-primary" />
            Labor Categories
          </h1>
          <p className="text-bw-muted mt-1">
            Define your labor categories with base rates, SCA mappings, and qualifications
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center px-4 py-2 bg-bw-primary text-white rounded-lg hover:bg-bw-primary transition-colors disabled:opacity-50"
        >
          {saving ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          Save Categories
        </button>
      </div>

      {/* Wrap Rate Info */}
      <div className="bg-bw-surface-subtle rounded-lg p-4 border border-blue-200 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Info className="h-5 w-5 text-bw-primary mr-2" />
            <div>
              <span className="text-sm font-medium text-bw-primary">Current Wrap Rate: </span>
              <span className="text-lg font-bold text-bw-primary">{calculateWrapRate().toFixed(3)}x</span>
            </div>
          </div>
          <div className="text-sm text-bw-primary">
            $100/hr base = <span className="font-semibold">${calculateLoadedRate(100).toFixed(2)}/hr loaded</span>
          </div>
        </div>
      </div>

      {/* Industry Suggestions */}
      <div className="bg-bw-surface rounded-xl border border-bw-border p-4 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <Sparkles className="h-5 w-5 text-bw-primary mr-2" />
            <h3 className="font-semibold text-bw-ink">AI-Suggested Labor Categories</h3>
          </div>
          <select
            value={selectedIndustry}
            onChange={(e) => setSelectedIndustry(e.target.value)}
            className="px-3 py-1.5 text-sm border border-bw-border rounded-lg focus:ring-2 focus:ring-bw-primary focus:border-transparent"
          >
            <option value="it_services">IT Services</option>
            <option value="professional_services">Professional Services</option>
            <option value="maintenance">Maintenance & Facilities</option>
            <option value="transportation">Transportation & Logistics</option>
            <option value="food_services">Food Services</option>
            <option value="security">Security Services</option>
          </select>
        </div>
        <div className="flex flex-wrap gap-2">
          {filteredSuggestions.map((suggestion, index) => {
            const isAdded = categories.some(
              cat => cat.title.toLowerCase() === suggestion.title.toLowerCase()
            );
            return (
              <button
                key={index}
                onClick={() => !isAdded && addFromSuggestion(suggestion)}
                disabled={isAdded}
                className={`px-3 py-1.5 rounded-full text-sm flex items-center transition-colors ${
                  isAdded
                    ? 'bg-success-bg text-success cursor-default'
                    : 'bg-bw-surface border border-purple-200 text-bw-primary hover:bg-bw-primary-tint'
                }`}
              >
                {isAdded ? (
                  <CheckCircle className="h-3 w-3 mr-1" />
                ) : (
                  <Plus className="h-3 w-3 mr-1" />
                )}
                {suggestion.title}
                <span className="ml-1 text-xs text-bw-muted">${suggestion.baseRate}/hr</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Labor Categories List */}
      <div className="bg-bw-surface rounded-xl border border-bw-border overflow-hidden">
        <div className="p-4 border-b border-bw-border flex items-center justify-between">
          <h3 className="font-semibold text-bw-ink">Your Labor Categories ({categories.length})</h3>
          <button
            onClick={addCategory}
            className="flex items-center px-3 py-1.5 text-sm text-bw-primary hover:bg-bw-primary-tint rounded-lg transition-colors"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Custom Category
          </button>
        </div>

        {categories.length === 0 ? (
          <div className="text-center py-12">
            <Users className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-bw-muted">No labor categories configured</p>
            <p className="text-sm text-bw-muted mt-1">
              Click the suggestions above or add a custom category
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {categories.map((category, index) => (
              <div
                key={category.id}
                className={`p-4 ${category.isEditing ? 'bg-bw-primary-tint' : 'hover:bg-bw-surface-subtle'} transition-colors`}
              >
                {category.isEditing ? (
                  // Edit mode
                  <div className="space-y-4">
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-bw-ink mb-1">Job Title *</label>
                        <input
                          type="text"
                          value={category.title}
                          onChange={(e) => updateCategory(category.id, { title: e.target.value })}
                          placeholder="e.g., Senior Software Engineer"
                          className="w-full px-3 py-2 border border-bw-border rounded-lg text-sm focus:ring-2 focus:ring-bw-primary focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-bw-ink mb-1">Base Rate ($/hr)</label>
                        <div className="relative">
                          <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-bw-muted" />
                          <input
                            type="number"
                            value={category.base_hourly_rate}
                            onChange={(e) => updateCategory(category.id, { base_hourly_rate: parseFloat(e.target.value) || 0 })}
                            className="w-full pl-8 pr-3 py-2 border border-bw-border rounded-lg text-sm focus:ring-2 focus:ring-bw-primary focus:border-transparent"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-bw-ink mb-1">GSA Rate ($/hr)</label>
                        <div className="relative">
                          <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-bw-muted" />
                          <input
                            type="number"
                            value={category.gsa_rate || ''}
                            onChange={(e) => updateCategory(category.id, { gsa_rate: parseFloat(e.target.value) || null })}
                            placeholder="Optional"
                            className="w-full pl-8 pr-3 py-2 border border-bw-border rounded-lg text-sm focus:ring-2 focus:ring-bw-primary focus:border-transparent"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-4 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-bw-ink mb-1">Min Experience (Years)</label>
                        <input
                          type="number"
                          min="0"
                          value={category.years_experience_min}
                          onChange={(e) => updateCategory(category.id, { years_experience_min: parseInt(e.target.value) || 0 })}
                          className="w-full px-3 py-2 border border-bw-border rounded-lg text-sm focus:ring-2 focus:ring-bw-primary focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-bw-ink mb-1">Education</label>
                        <input
                          type="text"
                          value={category.education_requirement || ''}
                          onChange={(e) => updateCategory(category.id, { education_requirement: e.target.value || null })}
                          placeholder="e.g., Bachelor's Degree"
                          className="w-full px-3 py-2 border border-bw-border rounded-lg text-sm focus:ring-2 focus:ring-bw-primary focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-bw-ink mb-1">Clearance</label>
                        <select
                          value={category.clearance_level || 'none'}
                          onChange={(e) => updateCategory(category.id, { clearance_level: e.target.value })}
                          className="w-full px-3 py-2 border border-bw-border rounded-lg text-sm focus:ring-2 focus:ring-bw-primary focus:border-transparent"
                        >
                          {CLEARANCE_LEVELS.map(level => (
                            <option key={level.value} value={level.value}>{level.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-bw-ink mb-1">SCA Code</label>
                        <input
                          type="text"
                          value={category.sca_occupation_code || ''}
                          onChange={(e) => updateCategory(category.id, { sca_occupation_code: e.target.value || null })}
                          placeholder="e.g., 14074"
                          className="w-full px-3 py-2 border border-bw-border rounded-lg text-sm focus:ring-2 focus:ring-bw-primary focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-bw-ink mb-1">Description</label>
                      <textarea
                        value={category.description || ''}
                        onChange={(e) => updateCategory(category.id, { description: e.target.value })}
                        placeholder="Brief description of responsibilities..."
                        rows={2}
                        className="w-full px-3 py-2 border border-bw-border rounded-lg text-sm focus:ring-2 focus:ring-bw-primary focus:border-transparent"
                      />
                    </div>

                    <div className="flex items-center justify-between pt-2">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={category.is_key_personnel}
                          onChange={(e) => updateCategory(category.id, { is_key_personnel: e.target.checked })}
                          className="rounded border-bw-border text-bw-primary focus:ring-bw-primary"
                        />
                        <span className="ml-2 text-sm text-bw-ink">Key Personnel</span>
                      </label>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => deleteCategory(category.id)}
                          className="px-3 py-1.5 text-sm text-urgent hover:bg-urgent-bg rounded-lg transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => {
                            updateCategory(category.id, { isEditing: false });
                            setEditingId(null);
                          }}
                          className="px-4 py-1.5 text-sm bg-bw-primary text-white rounded-lg hover:bg-bw-primary transition-colors"
                        >
                          Done
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  // View mode
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="w-8 text-center text-bw-muted text-sm">{index + 1}</div>
                      <div>
                        <div className="flex items-center">
                          <h4 className="font-medium text-bw-ink">{category.title}</h4>
                          {category.is_key_personnel && (
                            <span className="ml-2 px-1.5 py-0.5 bg-fit-moderate-bg text-fit-moderate text-xs rounded">
                              Key Personnel
                            </span>
                          )}
                        </div>
                        <div className="flex items-center space-x-4 text-sm text-bw-muted mt-1">
                          {category.years_experience_min > 0 && (
                            <span className="flex items-center">
                              <Briefcase className="h-3 w-3 mr-1" />
                              {category.years_experience_min}+ years
                            </span>
                          )}
                          {category.education_requirement && (
                            <span className="flex items-center">
                              <GraduationCap className="h-3 w-3 mr-1" />
                              {category.education_requirement}
                            </span>
                          )}
                          {category.clearance_level && category.clearance_level !== 'none' && (
                            <span className="flex items-center">
                              <Shield className="h-3 w-3 mr-1" />
                              {CLEARANCE_LEVELS.find(l => l.value === category.clearance_level)?.label}
                            </span>
                          )}
                          {category.sca_occupation_code && (
                            <span className="text-xs bg-bw-surface-subtle px-1.5 py-0.5 rounded">
                              SCA: {category.sca_occupation_code}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-6">
                      <div className="text-right">
                        <div className="text-sm font-medium text-bw-ink">
                          ${category.base_hourly_rate.toFixed(2)}/hr base
                        </div>
                        <div className="text-xs text-success">
                          ${calculateLoadedRate(category.base_hourly_rate).toFixed(2)}/hr loaded
                        </div>
                      </div>
                      {category.gsa_rate && (
                        <div className="text-right">
                          <div className="text-xs text-bw-muted">GSA Rate</div>
                          <div className="text-sm font-medium text-bw-primary">
                            ${category.gsa_rate.toFixed(2)}/hr
                          </div>
                        </div>
                      )}
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => {
                            updateCategory(category.id, { isEditing: true });
                            setEditingId(category.id);
                          }}
                          className="p-2 text-bw-muted hover:text-bw-primary transition-colors"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => deleteCategory(category.id)}
                          className="p-2 text-bw-muted hover:text-urgent transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Summary */}
      {categories.length > 0 && (
        <div className="mt-6 bg-bw-surface-subtle rounded-lg p-4">
          <h4 className="font-medium text-bw-ink mb-3">Summary</h4>
          <div className="grid grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-bw-muted">Total Categories:</span>
              <span className="ml-2 font-medium">{categories.length}</span>
            </div>
            <div>
              <span className="text-bw-muted">Avg Base Rate:</span>
              <span className="ml-2 font-medium">
                ${(categories.reduce((sum, c) => sum + c.base_hourly_rate, 0) / categories.length).toFixed(2)}/hr
              </span>
            </div>
            <div>
              <span className="text-bw-muted">Avg Loaded Rate:</span>
              <span className="ml-2 font-medium">
                ${(categories.reduce((sum, c) => sum + calculateLoadedRate(c.base_hourly_rate), 0) / categories.length).toFixed(2)}/hr
              </span>
            </div>
            <div>
              <span className="text-bw-muted">Key Personnel:</span>
              <span className="ml-2 font-medium">
                {categories.filter(c => c.is_key_personnel).length}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
